/** Shop-facing username rules (mirrors backend UsernameRules + ContentModeration). */

import { containsBlockedContent } from './contentModeration';

export const USERNAME_MIN_LENGTH = 3;

const RESERVED_BRAND_TOKENS = ['petito'] as const;

export function containsReservedBrandToken(username: string | null | undefined): boolean {
    if (!username?.trim()) return false;
    const name = username.trim().toLowerCase();
    return RESERVED_BRAND_TOKENS.some((token) => name.includes(token));
}

export { containsBlockedContent };

export function looksLikeEmail(username: string | null | undefined): boolean {
    if (!username?.trim()) return false;
    const t = username.trim();
    if (t.includes(' ')) return false;
    const at = t.indexOf('@');
    return at > 0 && at < t.length - 1;
}

/** Card / feed label: unique @handle. Leaves “Anna V.” and fallbacks alone. */
export function formatPublicHandle(name: string | null | undefined, fallback = 'Member'): string {
    const n = name?.trim() || '';
    if (!n) return fallback;
    if (n.startsWith('@')) return n;
    if (n.includes(' ')) return n;
    if (n.toLowerCase() === fallback.toLowerCase() || n.toLowerCase() === 'member') return n;
    return `@${n}`;
}

export function isUsernameTooShort(username: string | null | undefined): boolean {
    const name = username?.trim() ?? '';
    return name.length > 0 && name.length < USERNAME_MIN_LENGTH;
}

export function isDisallowedUsername(username: string | null | undefined): boolean {
    return looksLikeEmail(username) || containsReservedBrandToken(username) || containsBlockedContent(username);
}

export function getUsernameDisallowMessage(username: string | null | undefined): string | null {
    if (isUsernameTooShort(username)) {
        return 'Username must be at least 3 characters.';
    }
    if (looksLikeEmail(username)) {
        return 'Please choose a username that is not an email address.';
    }
    if (containsReservedBrandToken(username)) {
        return 'This username is reserved. Please choose a different name.';
    }
    if (containsBlockedContent(username)) {
        return 'This username is not allowed. Please choose a different name.';
    }
    return null;
}

export function isPersonNameDisallowed(name: string | null | undefined): boolean {
    return looksLikeEmail(name) || containsReservedBrandToken(name) || containsBlockedContent(name);
}

export function getPersonNameDisallowMessage(
    name: string | null | undefined,
    field: string
): string | null {
    if (!name?.trim()) return `${field} is required.`;
    if (looksLikeEmail(name)) return `${field} cannot be an email address.`;
    if (containsReservedBrandToken(name)) {
        return `This ${field.toLowerCase()} is reserved. Please choose a different name.`;
    }
    if (containsBlockedContent(name)) {
        return `This ${field.toLowerCase()} is not allowed. Please choose a different name.`;
    }
    return null;
}

function slugName(value: string | null | undefined): string {
    if (!value?.trim()) return '';
    return value.trim().toLowerCase().replace(/[^\p{L}\p{Nd}]/gu, '');
}

export function suggestShortHandle(firstName: string, lastName: string): string | null {
    const first = slugName(firstName);
    const last = slugName(lastName);
    if (!first) return null;
    const handle = last ? `${first}.${last[0]}` : first;
    if (handle.length < USERNAME_MIN_LENGTH) return null;
    if (isDisallowedUsername(handle)) return null;
    return handle;
}

export function suggestLongHandle(firstName: string, lastName: string): string | null {
    const first = slugName(firstName);
    const last = slugName(lastName);
    if (!first || last.length < 2) return null;
    const handle = `${first}.${last}`;
    const shortHandle = suggestShortHandle(firstName, lastName);
    if (shortHandle && handle.toLowerCase() === shortHandle.toLowerCase()) return null;
    if (handle.length < USERNAME_MIN_LENGTH) return null;
    if (isDisallowedUsername(handle)) return null;
    return handle;
}

/** Full chip set is 2 random + 1 personal — only when both names are present. */
export function canSuggestUsernames(firstName?: string | null, lastName?: string | null): boolean {
    return !!firstName?.trim() && !!lastName?.trim();
}

export function suggestHandles(firstName: string, lastName: string): string[] {
    const list: string[] = [];
    const shortHandle = suggestShortHandle(firstName, lastName);
    if (shortHandle) list.push(shortHandle);
    const longHandle = suggestLongHandle(firstName, lastName);
    if (longHandle) list.push(longHandle);
    return list;
}

export function personLabel(firstName: string | null | undefined, lastName: string | null | undefined): string | null {
    const first = firstName?.trim();
    if (!first || isPersonNameDisallowed(first)) return null;
    const last = lastName?.trim();
    const lastInitial =
        last && !isPersonNameDisallowed(last) ? ` ${last[0]!.toUpperCase()}.` : '';
    return `${first}${lastInitial}`;
}

export function isShortNameHandle(
    userName: string | null | undefined,
    firstName: string | null | undefined,
    lastName: string | null | undefined
): boolean {
    const handle = userName?.trim();
    const shortHandle = suggestShortHandle(firstName || '', lastName || '');
    if (!handle || !shortHandle) return false;
    return handle.toLowerCase() === shortHandle.toLowerCase();
}

export function showPersonLineOnProfile(
    userName: string | null | undefined,
    firstName: string | null | undefined,
    lastName: string | null | undefined
): boolean {
    return (
        !!personLabel(firstName, lastName) &&
        !isShortNameHandle(userName, firstName, lastName) &&
        !!userName?.trim() &&
        !looksLikeEmail(userName)
    );
}

/** Title is always the unique @handle when present. Person line is a subtitle. */
export function publicProfileHeading(opts: {
    userName?: string | null;
    displayName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    showPersonLine?: boolean;
}): { title: string; subtitle: string | null } {
    const handle =
        opts.userName?.trim() && !looksLikeEmail(opts.userName) ? opts.userName.trim() : '';
    const person =
        opts.displayName?.trim() || personLabel(opts.firstName, opts.lastName) || '';
    const showLine =
        opts.showPersonLine ?? showPersonLineOnProfile(opts.userName, opts.firstName, opts.lastName);
    if (handle) {
        return { title: `@${handle}`, subtitle: showLine ? person || null : null };
    }
    return { title: person || 'Member', subtitle: null };
}
